import React, { useEffect, useMemo, useState } from "react";
import * as Mui from "@mui/material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { PanelActionFooter, PrimaryButton } from "components/CustomComponents/DesignSystemButtons";
import { designColors, designType } from "services/constants/designTokens";
import { checkFormulaSyntax } from "services/utils/formulaUtils";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { FormulaEditor, PreviewBox, PreviewLine, Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { FormulaChip, SwitchLine, formatInr } from "pages/PayRoll/Shared/statutoryUi";
import {
  CHALLAN_ACCOUNTS,
  FORMULA_FUNCTIONS,
  REQUIRED_CALCULATION_CODES,
  REQUIRED_CONTRIBUTION_CODES,
  TEST_EMPLOYEES,
  checkPfFormula,
  displayFormula,
  runComponents,
} from "./pfConfigData";

// Add / Edit drawer for one PF calculation or contribution component
// (B3.1: same pattern as ESI). Both kinds use the formula editor with code
// chips. Contribution rates are written in the formula itself (e.g.
// ROUND(EPF_WAGE * 12%)) - there is no separate rates card. "Test on
// employee" works the formula out for a sample employee.

const REQUIRED = new Set([...REQUIRED_CALCULATION_CODES, ...REQUIRED_CONTRIBUTION_CODES]);

const emptyComponent = (kind) => ({
  code: "",
  name: "",
  kind,
  formula: "",
  used_for: "",
  challan_account: kind === "CONTRIBUTION" ? "1" : "none",
  status: "Active",
});

// Why the salary component codes (BASIC, HRA, ...) are missing from the
// formula chips, instead of leaving only the PF codes without a word.
const SalaryCodesNote = ({ status, count, onRetry }) => {
  const state = status?.state;
  let text = "";
  if (state === "loading") text = "Loading the salary component codes...";
  else if (state === "no-company") text = "Pick a company in the top bar to see its salary component codes.";
  else if (state === "error") text = `Salary component codes couldn't load: ${status.message}`;
  else if (state === "ready" && !count) text = "This company has no active salary components, so only PF codes are shown.";
  if (!text) return null;
  return (
    <Mui.Stack direction="row" spacing={1} alignItems="center" useFlexGap flexWrap="wrap" sx={{ mb: 1 }}>
      <Mui.Typography sx={{ ...designType.helperText, color: state === "error" ? "#DC2626" : designColors.slate600 }}>{text}</Mui.Typography>
      {state === "error" && onRetry ? (
        <Mui.Link component="button" type="button" onClick={onRetry} sx={{ fontSize: 13, fontWeight: 600 }}>
          Retry
        </Mui.Link>
      ) : null}
    </Mui.Stack>
  );
};

// salaryCodes: the company's salary component codes (BASIC, DA, ...),
// offered first in the formula editor.
export const PfComponentPanel = ({
  open,
  mode,
  kind,
  component,
  form,
  salaryCodes = [],
  salaryCodesStatus,
  onRetrySalaryCodes,
  readOnly,
  onClose,
  onSave,
}) => {
  const [draft, setDraft] = useState(() => component || emptyComponent(kind));
  const [showErrors, setShowErrors] = useState(false);
  const [employeeId, setEmployeeId] = useState(TEST_EMPLOYEES[0].id);

  useEffect(() => {
    if (!open) return;
    setDraft(component ? { ...component } : emptyComponent(kind));
    setShowErrors(false);
  }, [open, component, kind]);

  const adding = mode === "add";
  const isContribution = draft.kind === "CONTRIBUTION";
  const required = REQUIRED.has(draft.code) && !adding;
  const fixed = Boolean(draft.fixed);
  const locked = readOnly || fixed;
  const formulaEditable = !locked;
  const others = useMemo(
    () => form.components.filter((item) => adding || item.code !== component?.code),
    [form.components, adding, component],
  );

  const chipCodes = useMemo(
    () =>
      isContribution
        ? [...new Set(others.filter((item) => item.kind === "CALCULATION" && item.code).map((item) => item.code))]
        : salaryCodes,
    [isContribution, salaryCodes, others],
  );

  const set = (key) => (value) => setDraft((current) => ({ ...current, [key]: key === "code" ? String(value).toUpperCase() : value }));

  const formulaCheck = useMemo(
    () => (fixed ? { valid: true, message: "" } : checkPfFormula(draft.formula, others, draft, salaryCodes)),
    [fixed, draft, others, salaryCodes],
  );

  const errors = useMemo(() => {
    const result = {};
    if (!String(draft.name).trim()) result.name = "Enter a name";
    else if (/%/.test(draft.name)) result.name = "Keep percentages out of the name";
    if (adding) {
      if (!/^[A-Z][A-Z0-9_]{1,19}$/.test(draft.code)) result.code = "2–20 characters: capitals, digits and _ , starting with a letter";
      else if (form.components.some((item) => item.code === draft.code)) result.code = "This code is already used";
    }
    if (!formulaCheck.valid) result.formula = formulaCheck.message;
    return result;
  }, [draft, adding, form.components, formulaCheck]);
  const error = (key) => (showErrors ? errors[key] : undefined);

  const employee = TEST_EMPLOYEES.find((item) => item.id === employeeId) || TEST_EMPLOYEES[0];
  const test = useMemo(() => {
    if (!formulaCheck.valid || !draft.code) return null;
    const values = runComponents(form, employee, { ...draft, status: "Active" }, salaryCodes);
    const reads = checkFormulaSyntax(String(draft.formula).replace(/ROUNDUP\(/gi, "CEIL(").replace(/ROUNDDOWN\(/gi, "FLOOR(")).codes;
    return { values, reads: [...new Set(reads.map((code) => code.toUpperCase()))] };
  }, [formulaCheck.valid, draft, form, employee, salaryCodes]);

  const save = () => {
    setShowErrors(true);
    if (Object.keys(errors).length) return;
    onSave({ ...draft, name: draft.name.trim(), formula: String(draft.formula).trim() });
  };

  const title = adding
    ? `Add ${isContribution ? "Contribution" : "Calculation"} Component`
    : `${locked ? "" : "Edit "}${draft.name || draft.code}`;

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title={title}
      subtitle={`${isContribution ? "Contribution" : "Calculation"} component · ${form.name}`}
      bodySx={panelBodySx}
      footer={
        locked ? (
          <Mui.Stack direction="row" justifyContent="flex-end" sx={{ width: "100%" }}>
            <PrimaryButton onClick={onClose}>Close</PrimaryButton>
          </Mui.Stack>
        ) : (
          <PanelActionFooter onCancel={onClose} primaryLabel={adding ? "Add Component" : "Apply"} primaryProps={{ onClick: save }} />
        )
      }
    >
      <Mui.Stack spacing={2}>
        {fixed ? (
          <RequestInfoBanner
            title="Fixed component"
            body="The gross earnings of the finalised payroll for the month, so it can never drift from the payslip. Not editable."
          />
        ) : readOnly ? (
          <RequestInfoBanner tone="warning" title="This version is locked." body="A payroll has used it. Create a new version to change components." />
        ) : isContribution ? (
          <RequestInfoBanner
            title="The rate is part of the formula."
            body="Write the percentage in the formula, e.g. ROUND(EPF_WAGE * 12%). Change it here when the rate changes, in a new version."
          />
        ) : (
          <RequestInfoBanner
            title="Wage base used by the contribution components."
            body="Salary component codes (e.g. BASIC, DA) are the earned amounts after unpaid days. Settings such as CEILING and EPS_STOP_AGE come from the Wage Ceiling card."
          />
        )}

        <FormSection title="Component Details" info="How the component is named and where its amount goes.">
          <Field wide label={<>Component Name<Required /></>} helper="Plain words, no percentages (e.g. Employer — EPS)." error={error("name")}>
            <TextField value={draft.name} onChange={set("name")} disabled={locked} error={error("name")} placeholder="e.g. Retaining Wage" />
          </Field>
          <Field
            wide
            label={<>Component Code<Required /></>}
            helper={adding ? "Used in formulas. Can't be changed after saving." : "Locked: other formulas use it."}
            error={error("code")}
          >
            <TextField value={draft.code} onChange={set("code")} disabled={!adding} error={error("code")} placeholder="e.g. RET_WAGE" />
          </Field>
          <Field wide label="Used for" helper="Shown in the components table.">
            <TextField value={draft.used_for} onChange={set("used_for")} disabled={locked} placeholder="e.g. Employer cost · A/c 1" />
          </Field>
          {isContribution ? (
            <Field label="Challan Account" helper="The EPFO account the amount is paid into.">
              <SelectField value={draft.challan_account} onChange={set("challan_account")} options={CHALLAN_ACCOUNTS} disabled={locked} />
            </Field>
          ) : null}
          <Field label="Status" helper={required ? "Required by PF: always active." : "Inactive components are skipped."}>
            <SwitchLine
              checked={draft.status === "Active"}
              onChange={(checked) => set("status")(checked ? "Active" : "Inactive")}
              disabled={locked || required}
              label={draft.status}
            />
          </Field>
        </FormSection>

        <FormSection title="Formula" info="Codes, numbers, percentages (12%), + − × ÷, brackets, ROUND, ROUNDUP, ROUNDDOWN, MIN, MAX and IF. Up to 500 characters.">
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            {formulaEditable && !isContribution ? <SalaryCodesNote status={salaryCodesStatus} count={salaryCodes.length} onRetry={onRetrySalaryCodes} /> : null}
            {formulaEditable ? (
              <FormulaEditor
                value={draft.formula}
                onChange={set("formula")}
                // Calculation: salary component codes from the API.
                // Contribution: only this version's Calculation Component
                // codes. Other valid codes are still accepted when typed.
                codes={chipCodes}
                functions={FORMULA_FUNCTIONS}
                check={formulaCheck}
                error={error("formula")}
              />
            ) : (
              <Mui.Stack spacing={1.5}>
                <Mui.Box>
                  <FormulaChip formula={displayFormula(draft)} />
                </Mui.Box>
              </Mui.Stack>
            )}
          </Mui.Box>
        </FormSection>

        <FormSection title="Test on Employee" info="Works the formula out with this version's settings for a sample employee.">
          <Field wide label="Employee">
            <SelectField
              value={employeeId}
              onChange={(value) => setEmployeeId(value || TEST_EMPLOYEES[0].id)}
              options={TEST_EMPLOYEES.map((item) => ({ value: item.id, label: `${item.name} · ${item.id}` }))}
            />
          </Field>
          <Mui.Box sx={{ gridColumn: "1 / -1" }}>
            {test ? (
              <PreviewBox>
                {test.reads.map((code) => (
                  <PreviewLine
                    key={code}
                    label={code}
                    value={formatInr(test.values[code])}
                  />
                ))}
                <PreviewLine label={`${draft.code} =`} value={`₹${formatInr(test.values[draft.code])}`} strong divider />
              </PreviewBox>
            ) : (
              <Mui.Typography sx={{ ...designType.helperText, color: designColors.slate600 }}>
                Enter a valid code and formula to test it.
              </Mui.Typography>
            )}
          </Mui.Box>
        </FormSection>
      </Mui.Stack>
    </GlobalSidePanel>
  );
};
